import React, { useState, useEffect } from 'react';
import './OperatingHours.css';
import { getOperatingHours, saveOperatingHours } from '../../../utils/operatingHours';

const OperatingHours = ({ onSave, onChanges }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [hours, setHours] = useState({
    monday: { enabled: true, open: '11:00', close: '23:00' },
    tuesday: { enabled: true, open: '11:00', close: '23:00' },
    wednesday: { enabled: true, open: '11:00', close: '23:00' },
    thursday: { enabled: true, open: '11:00', close: '23:00' },
    friday: { enabled: true, open: '11:00', close: '23:00' },
    saturday: { enabled: true, open: '11:00', close: '23:00' },
    sunday: { enabled: false, open: '11:00', close: '23:00' }
  });
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  // Load existing operating hours on component mount
  useEffect(() => {
    loadOperatingHours();
  }, []);

  const loadOperatingHours = async () => {
    try {
      setFetching(true);
      const { data, error } = await getOperatingHours();
      
      if (error) {
        console.error('Error loading operating hours:', error);
        // Don't show error to user, just use default values
        setFetching(false);
        return;
      }

      if (data) {
        setIsOpen(data.is_open !== undefined ? data.is_open : true);
        
        // Load hours if they exist
        if (data.operating_hours) {
          setHours(data.operating_hours);
        }
        
        // Load holidays if they exist
        if (data.holidays && Array.isArray(data.holidays)) {
          setHolidays(data.holidays);
        }
      }
    } catch (error) {
      console.error('Error loading operating hours:', error);
    } finally {
      setFetching(false);
    }
  };

  const days = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  const handleDayChange = (day, field, value) => {
    setHours(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
    onChanges();
  };

  const copyToAllDays = () => {
    const mondayHours = hours.monday;
    const newHours = {};
    days.forEach(day => {
      newHours[day.key] = { ...mondayHours, enabled: hours[day.key].enabled };
    });
    setHours(newHours);
    onChanges();
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      console.log('Saving operating hours to Supabase...');
      
      const { data, error } = await saveOperatingHours(isOpen, hours, holidays);
      
      if (error) {
        console.error('❌ Error saving operating hours:', error);
        const errorMessage = error.message || 'Failed to save operating hours. Please try again.';
        alert(`Error: ${errorMessage}`);
        return;
      }

      if (!data) {
        console.error('❌ No data returned from save operation');
        alert('Failed to save operating hours. No data returned.');
        return;
      }

      console.log('✅ Operating hours saved successfully to Supabase!');
      onSave();
    } catch (error) {
      console.error('❌ Unexpected error saving operating hours:', error);
      alert(`Failed to save operating hours: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="settings-section">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading operating hours...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2 className="section-title">Operating Hours</h2>
        <p className="section-subtitle">Set your restaurant's working hours</p>
        <div className="section-divider"></div>
      </div>

      <div className="form-content">
        <div className="status-toggle-card">
          <div className="status-header">
            <label className="status-label">Restaurant Status</label>
            <div className="large-toggle-wrapper">
              <input
                type="checkbox"
                className="large-toggle"
                checked={isOpen}
                onChange={(e) => {
                  setIsOpen(e.target.checked);
                  onChanges();
                }}
                disabled={loading}
              />
              <span className={`status-text ${isOpen ? 'open' : 'closed'}`}>
                ● {isOpen ? 'Currently Open' : 'Currently Closed'}
              </span>
            </div>
          </div>
          <p className="status-helper">Toggle to temporarily close restaurant</p>
        </div>

        <div className="hours-section">
          <div className="copy-hours-btn-wrapper">
            <button className="copy-hours-btn" onClick={copyToAllDays}>
              Copy to All Days
            </button>
          </div>

          {days.map(day => (
            <div key={day.key} className="day-row">
              <div className="day-toggle-col">
                <label className="day-checkbox-label">
                  <input
                    type="checkbox"
                    checked={hours[day.key].enabled}
                    onChange={(e) => handleDayChange(day.key, 'enabled', e.target.checked)}
                    className="day-checkbox"
                  />
                  <span className="day-name">{day.label}</span>
                </label>
              </div>
              <div className="time-col">
                <label className="time-label">Opens</label>
                <input
                  type="time"
                  value={hours[day.key].open}
                  onChange={(e) => handleDayChange(day.key, 'open', e.target.value)}
                  className="time-input"
                  disabled={!hours[day.key].enabled}
                />
              </div>
              <div className="time-col">
                <label className="time-label">Closes</label>
                <input
                  type="time"
                  value={hours[day.key].close}
                  onChange={(e) => handleDayChange(day.key, 'close', e.target.value)}
                  className="time-input"
                  disabled={!hours[day.key].enabled}
                />
              </div>
              <div className="status-col">
                <span className={`status-badge ${hours[day.key].enabled ? 'open' : 'closed'}`}>
                  {hours[day.key].enabled ? 'Open' : 'Closed'}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="holidays-section">
          <h3 className="holidays-title">Special Days / Holidays</h3>
          <button className="add-holiday-btn">
            + Add Holiday
          </button>
          {holidays.length > 0 && (
            <div className="holidays-list">
              {holidays.map((holiday, i) => (
                <div key={i} className="holiday-item">
                  <span>{new Date(holiday.date).toLocaleDateString()} - {holiday.name}</span>
                  <span className="holiday-status closed">Closed</span>
                  <button className="remove-holiday-btn">×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-actions">
          <button 
            className="save-btn" 
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OperatingHours;

